/**
 * Contenido de la Guía del demo en la consola (T-INT-05). Fuente: `docs/03-demo/01-guion-demo.md`,
 * `02-datos-semilla.md` y `03-guia-probadores.md`. Todo es ficticio.
 */

export const DEMO_OTP = '123456';

export const CONSOLE_USERS: readonly { email: string; role: string; can: string }[] = [
  { email: 'admin@demo.paguz.com', role: 'Administración', can: 'Todo, incluida la configuración' },
  {
    email: 'analista@demo.paguz.com',
    role: 'Analista de crédito',
    can: 'Expedientes: aprobar, rechazar o contraofertar',
  },
  {
    email: 'operador@demo.paguz.com',
    role: 'Operador de pagos',
    can: 'Transacciones, disputas y Controles de demo',
  },
  {
    email: 'conocimiento@demo.paguz.com',
    role: 'Administración de conocimiento',
    can: 'Base de conocimiento y skills del Asesor IA',
  },
  { email: 'auditor@demo.paguz.com', role: 'Auditoría', can: 'Solo lectura y auditoría' },
];

export const PERSONAS: readonly { name: string; phone: string; state: string }[] = [
  {
    name: 'Rosa Martínez',
    phone: '300 111 2233',
    state: 'Cupo $ 3.000.000, ≈ $ 2.150.000 disponible',
  },
  { name: 'Jairo Pérez', phone: '310 222 3344', state: 'Sin solicitud' },
  { name: 'Luz Dary Gómez', phone: '311 333 4455', state: 'Solicitud en EVIDENCE, falta extracto' },
  {
    name: 'Hernán Castillo',
    phone: '312 444 5566',
    state: 'En mora (≈ 12 días), compras bloqueadas',
  },
  { name: 'Marcela Rojas', phone: '313 555 6677', state: 'Solicitud rechazada con motivos' },
];

export interface Scenario {
  readonly n: number;
  readonly title: string;
  readonly who: string;
  readonly where: 'App' | 'Consola' | 'App + Consola' | 'Portal Comercio';
  readonly steps: readonly string[];
  readonly expect: string;
}

/** Escenas del guion (§1), en orden. */
export const SCENARIOS: readonly Scenario[] = [
  {
    n: 1,
    title: 'Compra con QR en cuotas',
    who: 'Rosa',
    where: 'App',
    steps: [
      'En la app, ingresa como Rosa (código 123456).',
      'Pagar → Abrir cámara: escanea el QR dinámico de Ferretería Don Pedro por $ 180.000.',
      'Elige 3 cuotas, revisa el resumen y confirma con el código.',
    ],
    expect: 'Comprobante en segundos; el Portal Comercio de Don Pedro avisa "¡Pago recibido!".',
  },
  {
    n: 2,
    title: 'Detrás de cámaras',
    who: 'Operador',
    where: 'Consola',
    steps: [
      'Transacciones → abre la compra de Rosa.',
      'Revisa la línea de tiempo: validación, retención de cupo, fondeo, Bre-B, captura y posteo.',
      'Clientes y líneas → Rosa: nueva utilización y su plan de cuotas.',
    ],
    expect: 'Cada paso con tiempos y traceId; todo conciliable y auditable.',
  },
  {
    n: 3,
    title: 'Compra conversacional (texto o voz)',
    who: 'Rosa',
    where: 'App',
    steps: [
      'Asesor IA, por texto o por voz: "Págale 250 mil a @mayoristadelsur en 2 cuotas".',
      'Revisa el resumen y confirma con el código.',
    ],
    expect: 'La IA prepara el resumen; el pago solo sale con la confirmación del cliente.',
  },
  {
    n: 4,
    title: 'Solicitud con el Asesor IA',
    who: 'Jairo',
    where: 'App',
    steps: [
      'Saludo con el proceso → conversación sobre el taller → Acepto las autorizaciones (sin pedir la cédula: la valida el banco).',
      'Ingresos y gastos del negocio y del hogar, personas a cargo y otras deudas → Sí, son correctos.',
      'Registro incompleto (Excel): el asesor explica qué no cuadra y cómo corregirlo; luego extracto bancario y registro de ingresos y gastos.',
      'Flujo de caja con fuentes → Sí, está bien → simulación → enviar a decisión.',
    ],
    expect:
      'Soportes en el orden estándar, cada revisión en menos de un minuto y en el mismo turno; el expediente llega completo a la consola.',
  },
  {
    n: 5,
    title: 'Decisión del analista',
    who: 'Analista',
    where: 'Consola',
    steps: [
      'Expedientes → Jairo: documentos con revisión IA (incluido el registro en Excel), flujo de caja, central de riesgo y recomendación.',
      'Aprobar (o contraoferta). Muestra en Configuración la política de decisión.',
    ],
    expect:
      'El cupo sugerido (≈ $ 3.000.000) supera el umbral de autoaprobación ($ 2.000.000): decide el humano.',
  },
  {
    n: 6,
    title: 'Formalización y primera compra',
    who: 'Jairo',
    where: 'App',
    steps: [
      'Ver la oferta con costo total, aceptar y firmar el pagaré (código 123456).',
      'Compra inmediata por QR en Repuestos Moto Express.',
    ],
    expect: 'De solicitud a primera compra en una sola experiencia digital.',
  },
  {
    n: 7,
    title: 'Memoria entre sesiones',
    who: 'Luz Dary',
    where: 'App',
    steps: ['Abrir el Asesor IA como Luz Dary y subir el extracto de octubre.'],
    expect:
      'Saluda recordando que faltaba el extracto de octubre; lo revisa en el mismo turno y sigue con el flujo de caja.',
  },
  {
    n: 8,
    title: 'Devolución del comercio',
    who: 'Comercio + Rosa',
    where: 'Portal Comercio',
    steps: [
      'Portal Comercio (o Controles de demo → Devolución) → compra de Rosa → Devolver parcial.',
      'En la app de Rosa: la utilización se recalcula y el cupo se restituye.',
    ],
    expect: 'La devolución por Bre-B se aplica al crédito, no queda como saldo suelto.',
  },
  {
    n: 9,
    title: 'El tiempo pasa',
    who: 'Operador',
    where: 'App + Consola',
    steps: [
      'Controles de demo → avanzar 30 días.',
      'Extracto de Rosa (pago mínimo, fecha límite) e intereses causados.',
      'Hernán intenta comprar → rechazo claro → paga su cuota → se desbloquea.',
    ],
    expect: 'Cuotas, intereses, extractos y mora funcionan como un core de crédito completo.',
  },
  {
    n: 11,
    title: 'Resiliencia',
    who: 'Operador',
    where: 'App + Consola',
    steps: [
      'Controles de demo → próxima transacción "Bre-B no responde".',
      'Haz una compra: queda "en verificación" y se resuelve sola.',
    ],
    expect: 'Nunca hay dinero en estado desconocido.',
  },
];

export const CENTS_FAULTS: readonly { cents: string; result: string }[] = [
  { cents: '.00', result: 'Normal: aceptada en segundos.' },
  { cents: '.13', result: 'Rechazada por el banco del comercio (MS03); se libera el cupo.' },
  { cents: '.77', result: 'Confirmación tardía a los 25 s (después del SLA de 20 s).' },
  { cents: '.99', result: 'Sin confirmación; a los 60 s la consulta la da por aceptada.' },
  { cents: '.98', result: 'Sin confirmación; a los 60 s la consulta la da por rechazada.' },
  { cents: '.55', result: 'Confirmación duplicada (mismo evento): se procesa una vez.' },
  { cents: '.31', result: 'Falla del core de depósitos (cuenta vinculada): la saga compensa.' },
];

/** Preparación antes de una sesión de prueba (guion §0). */
export const PREPARATION: readonly string[] = [
  'Controles de demo → Reset de datos (todo) y revisa que las verificaciones salgan en verde.',
  'Reloj en tiempo real y fallas Bre-B en modo Normal.',
  'Portal Comercio abierto en Ferretería Don Pedro (y otra pestaña en Distribuidora El Mayorista del Sur).',
  'Celular con la app abierta como Rosa y los documentos de muestra descargados (Guía de la app): extracto, registros en Excel, factura y recibo.',
  'Permiso de micrófono concedido en el navegador del celular y volumen arriba para la voz.',
];
